import classNames from 'classnames';
import React, {
  FC, SelectHTMLAttributes, useState, useRef, useEffect, useMemo, useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { AiOutlineQuestionCircle } from 'react-icons/ai';
import { IoIosArrowDown } from 'react-icons/io';
import Tooltip from '../Tooltip';
import './Select.css';

export interface Option {
  label: React.ReactNode;
  value: string | number;
}

interface CustomSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  id: string;
  label?: string;
  options: Option[];
  errMsg?: string;
  tooltipText?: string;
  placeholder?: string;
  /** Adds a search box on top of the list (filters options with a string label). */
  searchable?: boolean;
  searchPlaceholder?: string;
}

const normalize = (text: string) => text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const Select: FC<CustomSelectProps> = ({
  id, label, errMsg, tooltipText, options, placeholder = 'Seleccionar...', onChange, value, disabled,
  searchable = false, searchPlaceholder = 'Buscar...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Searchable mode: the option the arrow keys are on, Enter picks it.
  const [activeIndex, setActiveIndex] = useState(-1);
  const [selectedValue, setSelectedValue] = useState<string | number>(
    typeof value === 'string' || typeof value === 'number' ? value : '',
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const ITEM_HEIGHT = 44; // px; keep in sync with item padding
  const DROPDOWN_MAX_HEIGHT = 240; // max-h-60 (15rem)
  const VIEWPORT_PADDING = 16;
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(DROPDOWN_MAX_HEIGHT);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const SEARCH_HEIGHT = searchable ? 56 : 0;

  const visibleOptions = useMemo(() => {
    const q = normalize(query.trim());
    if (!searchable || !q) {
      return options;
    }
    return options.filter((opt) => typeof opt.label === 'string' && normalize(opt.label).includes(q));
  }, [options, query, searchable]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current?.contains(target)
        || listRef.current?.contains(target)
        || triggerRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setSelectedValue(typeof value === 'string' || typeof value === 'number' ? value : '');
  }, [value]);

  const updateDropdownPlacement = useCallback(() => {
    if (!triggerRef.current) {
      return;
    }

    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING;
    const spaceAbove = rect.top - VIEWPORT_PADDING;
    const estimatedHeight = Math.min(DROPDOWN_MAX_HEIGHT, options.length * ITEM_HEIGHT) + SEARCH_HEIGHT;
    const shouldOpenUpward = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

    setDropdownStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      maxHeight: DROPDOWN_MAX_HEIGHT,
      ...(shouldOpenUpward
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, [options.length, SEARCH_HEIGHT]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    updateDropdownPlacement();
    window.addEventListener('scroll', updateDropdownPlacement, true);
    window.addEventListener('resize', updateDropdownPlacement);

    return () => {
      window.removeEventListener('scroll', updateDropdownPlacement, true);
      window.removeEventListener('resize', updateDropdownPlacement);
    };
  }, [isOpen, updateDropdownPlacement]);

  const openDropdown = (initialQuery = '') => {
    // Opening upward covers the field's own label: when there's no room
    // below, bring the field to the middle of the screen first.
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const needed = Math.min(DROPDOWN_MAX_HEIGHT, options.length * ITEM_HEIGHT) + SEARCH_HEIGHT;
      if (window.innerHeight - rect.bottom - VIEWPORT_PADDING < needed) {
        triggerRef.current.scrollIntoView({ block: 'center' });
      }
    }
    setQuery(initialQuery);
    setActiveIndex(initialQuery ? 0 : -1);
    updateDropdownPlacement();
    setIsOpen(true);
    setTimeout(() => {
      if (listRef.current) {
        setContainerHeight(listRef.current.clientHeight || DROPDOWN_MAX_HEIGHT);
        const idx = initialQuery ? -1 : options.findIndex((o) => o.value === selectedValue);
        listRef.current.scrollTop = idx >= 0 ? idx * ITEM_HEIGHT : 0;
        // Always sync with the real position: a stale scrollTop from the
        // last opening rendered the wrong slice and left the list blank.
        setScrollTop(listRef.current.scrollTop);
      }
    }, 0);
  };

  const closeDropdown = (focusTrigger = false) => {
    setIsOpen(false);
    setScrollTop(0);
    setActiveIndex(-1);
    if (focusTrigger) {
      triggerRef.current?.focus();
    }
  };

  // Keeps the active option inside the (virtualized) list's viewport.
  const revealIndex = (index: number) => {
    const list = listRef.current;
    if (!list) {
      return;
    }
    const top = index * ITEM_HEIGHT;
    if (top < list.scrollTop) {
      list.scrollTop = top;
    } else if (top + ITEM_HEIGHT > list.scrollTop + list.clientHeight) {
      list.scrollTop = top + ITEM_HEIGHT - list.clientHeight;
    }
    setScrollTop(list.scrollTop);
  };

  const handleSelect = (newValue: string | number) => {
    setSelectedValue(newValue);
    if (onChange) {
      // Create a synthetic event to match the native select onChange
      const event = {
        target: { value: newValue },
      } as React.ChangeEvent<HTMLSelectElement>;
      onChange(event);
    }
    closeDropdown(searchable);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (visibleOptions.length === 0) {
        return;
      }
      const next = e.key === 'ArrowDown'
        ? Math.min(activeIndex + 1, visibleOptions.length - 1)
        : Math.max(activeIndex - 1, 0);
      setActiveIndex(next);
      revealIndex(next);
    } else if (e.key === 'Enter') {
      // Never submit the surrounding form from the search box.
      e.preventDefault();
      const option = visibleOptions[activeIndex >= 0 ? activeIndex : 0];
      if (option) {
        handleSelect(option.value);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeDropdown(true);
    } else if (e.key === 'Tab') {
      closeDropdown();
    }
  };

  const listboxId = `${id}-listbox`;
  const optionId = (index: number) => `${id}-option-${index}`;

  const selectedOption = options.find((opt) => opt.value === selectedValue);
  const displayValue: React.ReactNode = selectedOption ? selectedOption.label : placeholder;

  const buttonClasses = classNames(
    'w-full h-13 bg-surface-base p-3 border border-border-tertiary rounded-3xl',
    'flex items-center justify-between',
    'focus:outline-none focus:ring-0 focus:ring-primary-500',
    'hover:border-border-secondary',
    'dark:border-gradient-brand',
    'button-shadow',
    'transition-all duration-200',
    {
      'border-red-500 focus:ring-red-400': errMsg,
      'opacity-60 cursor-not-allowed': disabled,
    },
  );

  // Virtualization math
  const total = visibleOptions.length;
  const visibleCount = useMemo(
    () => Math.ceil(containerHeight / ITEM_HEIGHT) + 6, // overscan
    [containerHeight],
  );
  const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - 3);
  const endIndex = Math.min(total, startIndex + visibleCount);
  const topSpacer = startIndex * ITEM_HEIGHT;
  const bottomSpacer = Math.max(0, (total - endIndex) * ITEM_HEIGHT);

  return (
    <div className="form-control flex flex-col w-full relative" ref={dropdownRef}>
      {label && (
        <div className='flex items-center gap-1 w-full mb-1'>
          <label htmlFor={id} className="text-left font-sm font-medium leading-none">
            {label}
          </label>
          {tooltipText && (
            <Tooltip content={tooltipText}>
              <AiOutlineQuestionCircle className='h-4 w-4 shrink-0 text-primary-500' />
            </Tooltip>
          )}
        </div>
      )}
      <div className="relative w-full">
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          className={buttonClasses}
          onClick={() => {
            if (disabled) {
              return;
            }
            if (isOpen) {
              setIsOpen(false);
              setScrollTop(0);
              return;
            }
            openDropdown();
          }}
          onKeyDown={(e) => {
            if (!searchable || disabled || isOpen) {
              return;
            }
            // Typing on the closed field opens it already filtering by that
            // letter, so "san j" lands on San Juan without clicking.
            if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && e.key !== ' ') {
              e.preventDefault();
              openDropdown(e.key);
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              openDropdown();
            }
          }}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className='text-text-primary flex items-center gap-2'>{displayValue}</span>
          <IoIosArrowDown className={`text-text-tertiary transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && createPortal(
          <div
            style={{ ...dropdownStyle, maxHeight: undefined }}
            className="z-[9999] bg-surface-base border border-border-secondary rounded-3xl shadow-lg overflow-hidden isolation-isolate flex flex-col"
          >
            {searchable && (
              <div className="p-2 border-b border-border-secondary">
                <input
                  autoFocus
                  type="text"
                  role="combobox"
                  aria-expanded
                  aria-controls={listboxId}
                  aria-autocomplete="list"
                  aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
                  aria-label={label ? `${label}: ${searchPlaceholder}` : searchPlaceholder}
                  placeholder={searchPlaceholder}
                  value={query}
                  // Opened by typing on the field: the caret goes after that first letter.
                  onFocus={(e) => {
                    const { length } = e.currentTarget.value;
                    e.currentTarget.setSelectionRange(length, length);
                  }}
                  onKeyDown={handleSearchKeyDown}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    // The first match is ready for Enter.
                    setActiveIndex(e.target.value.trim() ? 0 : -1);
                    if (listRef.current) {
                      listRef.current.scrollTop = 0;
                    }
                    setScrollTop(0);
                  }}
                  className="w-full rounded-3xl border border-border-tertiary bg-surface-base px-4 py-2 text-md-regular text-text-primary placeholder:text-text-secondary focus:outline-none focus:border-primary-500"
                />
              </div>
            )}
          <div
            style={{ maxHeight: DROPDOWN_MAX_HEIGHT }}
            className="overflow-auto"
            id={listboxId}
            role="listbox"
            ref={listRef}
            onScroll={(e) => setScrollTop((e.target as HTMLDivElement).scrollTop)}
          >
            {total === 0 && (
              <div className="px-4 py-3 text-text-secondary">Sin resultados</div>
            )}
            <div style={{ height: topSpacer }} />
            {visibleOptions.slice(startIndex, endIndex).map((option, offset) => (
              <div
                key={option.value}
                id={optionId(startIndex + offset)}
                role="option"
                aria-selected={option.value === selectedValue}
                className={classNames(
                  'px-4 py-2 cursor-pointer text-text-primary',
                  'hover:bg-primary-500 hover:text-white',
                  'transition-colors duration-200',
                  {
                    'bg-primary-500 text-white': option.value === selectedValue,
                    'bg-surface-tertiary': option.value !== selectedValue && startIndex + offset === activeIndex,
                  },
                )}
                style={{ height: ITEM_HEIGHT }}
                onClick={() => handleSelect(option.value)}
              >
                <div className="flex items-center gap-2 h-full">{option.label}</div>
              </div>
            ))}
            <div style={{ height: bottomSpacer }} />
          </div>
          </div>,
          document.body,
        )}
      </div>

      {errMsg && (
        <span className="text-text-error text-sm mt-1">{errMsg}</span>
      )}
    </div>
  );
};

export default Select;
